import { EmptyArray, sortBy } from 'lib/array/arrayUtils'
import noop from 'lib/noop'
import { EmptyObject } from 'lib/objectUtils'
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { InView } from 'react-intersection-observer'
import styled from 'styled-components'
import ResponsiveImage, { ImageParams } from 'components/Common/ResponsiveImage'

const Carousel = styled.div`
  display: flex;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex: 1 0 100%;
    scroll-snap-align: center;
  }
`

const CarouselTrack = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  height: 100%;
`

const ExternalImage = styled.img`
  height: 100%;
  width: 100%;
  object-fit: cover;
  object-position: center;
`

const InternalImage = styled(ResponsiveImage)`
  height: 100%;
`

export interface ImageCarouselRefInterface {
  nextImage: () => void;
  previousImage: () => void;
  goToImage: (image: App.Image, method?: 'smooth' | 'instant') => void;
}

export interface ImageCarouselInterstitial {
  id: string;
  position: number | 'start' | 'end';
  element: React.ReactNode;
}

interface ImageCarouselItem {
  id: string;
  position: number | 'start' | 'end';
  type: 'interstital' | 'image';
  element?: React.ReactNode;
  image?: App.Image;
}

interface Props {
  defaultIndex?: number;
  images: Array<App.Image>;
  interstitials?: Array<ImageCarouselInterstitial>;
  className?: string;
  imageParams?: ImageParams;
  eagerLoadFirstImage?: boolean;
  carouselRef?: React.RefObject<ImageCarouselRefInterface>;
  onImageChange?: (index: number, image?: App.Image) => void;
}

function BaseImageCarousel(props: Props) {
  const {
    images,
    className,
    imageParams = EmptyObject as any,
    eagerLoadFirstImage,
    carouselRef,
    onImageChange = noop,
    interstitials = EmptyArray,
    defaultIndex = 0,
  } = props

  const [currentImageIndex, setCurrentImageIndex] = useState<number>(defaultIndex)
  const localCarouselRef = useRef<HTMLDivElement>(null)

  const imageItems = useMemo((): Array<ImageCarouselItem> => {
    const allItems = [
      ...interstitials.map((interstitial): ImageCarouselItem => ({
        id: interstitial.id,
        position: interstitial.position,
        element: interstitial.element,
        type: 'interstital',
      })),
      ...images.map((image, index): ImageCarouselItem => ({
        id: image.id ?? image.url ?? index.toString(),
        position: index + 1,
        image,
        type: 'image',
      })),
    ]

    return sortBy(allItems, item => {
      if (item.position === 'end') {
        return 10000
      } else if (item.position === 'start') {
        return -1
      } else {
        return item.position
      }
    }, 'asc')
  }, [interstitials, images])

  const moveSlide = useCallback((direction: 'previous' | 'next') => {
    if (localCarouselRef.current) {
      const offset = localCarouselRef.current.offsetWidth
      let nextLeft = Math.round(localCarouselRef.current.scrollLeft) + (direction === 'previous' ? -offset : offset)
      let behavior = 'smooth'
      if (nextLeft < 0) {
        nextLeft = localCarouselRef.current.scrollWidth
        // We don't have a nice implementation for when the next slide wraps around at the moment
        // so instead we need it to be instant or else it looks a bit funny as it scrolls between *all* the slides!
        behavior = 'instant'
      } else if (nextLeft > (localCarouselRef.current.scrollWidth - localCarouselRef.current.offsetWidth)) {
        nextLeft = 0
        behavior = 'instant'
      }

      localCarouselRef.current.scrollTo({
        left: nextLeft,
        // @ts-ignore 'instant' is still supported by browsers and works as we intend it.
        // Auto would still sometimes smooth scroll it
        behavior,
      })
    }
  }, [])

  const goToImage = (index: number, method: 'smooth' | 'instant' = 'instant') => {
    if (localCarouselRef.current) {
      const offset = localCarouselRef.current.offsetWidth
      const nextLeft = offset * index
      localCarouselRef.current.scrollTo({
        left: nextLeft,
        // @ts-ignore 'instant' is still supported by browsers and works as we intend it.
        // Auto would still sometimes smooth scroll it
        behavior: method,
      })
    }
  }

  useEffect(() => {
    if (defaultIndex > 0) {
      goToImage(defaultIndex)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useImperativeHandle(carouselRef, () => ({
    previousImage: () => moveSlide('previous'),
    nextImage: () => moveSlide('next'),
    goToImage: (image, method) => {
      const imageIndex = Math.max(imageItems.findIndex(item => item.image === image), 0)
      goToImage(imageIndex, method)
    },
  }))

  const onInViewChange = useCallback((inView: boolean, entry: IntersectionObserverEntry) => {
    if (inView) {
      const index = parseInt((entry.target as HTMLDivElement).dataset.index ?? '0')
      setCurrentImageIndex(index)
      onImageChange(index, imageItems[index].image)
    }
  }, [onImageChange, imageItems])

  const placeholderLowerBound = currentImageIndex - 2
  const placeholderUpperBound = currentImageIndex + 2

  return <CarouselTrack className={className}>
    <Carousel ref={localCarouselRef}>
      {imageItems.map((item, index) => <InView
        onChange={onInViewChange}
        threshold={0.5}
        key={item.id}
        data-index={index}
      >
        {item.type === 'interstital' && item.element}
        {item.type === 'image' && item.image && <>
          {(item.image.url && !item.image.id) && <ExternalImage
            src={item.image.url}
            alt={item.image.title}
          />}
          {item.image.id && <InternalImage
            {...imageParams}
            fit={imageParams.fit ?? 'center'}
            id={item.image.id}
            alt={item.image.title}
            loading={(eagerLoadFirstImage && index === 0) ? 'eager' : 'lazy'}
            withPlaceholder={
              index >= placeholderLowerBound &&
              index <= placeholderUpperBound}
          />}
        </>}
      </InView>)
      }
    </Carousel>
  </CarouselTrack>
}

export default React.memo(BaseImageCarousel)
